import React from "react";
import { Button, IconButton, Stack, Tooltip } from "@mui/material";
import { History, RotateCcw } from "lucide-react";
import type { LifecycleRetreat } from "../../../../types/retreatLifecycle";
import type { SectionKey } from "./manageRetreatTypes";
import { DASHBOARD_NAVY, dashboardOutlinedButtonSx } from "./dashboardUi";
import {
  RetreatDeleteIconButton,
  RetreatEditIconButton,
  RETREAT_ACTION_ROW_SPACING,
  retreatActionButtonSx,
} from "./RetreatDashboardActionIcons";

function retreatDisplayName(r: LifecycleRetreat) {
  return r.parishName || r.title || "retreat";
}

export function SectionActionButtons({
  r,
  kind,
  isSuperAdmin,
  onHistory,
  onDelete,
  onApprove,
  onRestore,
}: {
  r: LifecycleRetreat;
  kind: SectionKey;
  isSuperAdmin: boolean;
  onHistory: (r: LifecycleRetreat) => void;
  onDelete: (r: LifecycleRetreat) => void;
  onApprove: (r: LifecycleRetreat) => void;
  onRestore: (r: LifecycleRetreat) => void;
}) {
  const name = retreatDisplayName(r);

  return (
    <Stack
      direction="row"
      spacing={RETREAT_ACTION_ROW_SPACING}
      flexWrap="wrap"
      useFlexGap
      alignItems="center"
    >
      <Tooltip title="Retreat history" placement="bottom" arrow>
        <IconButton
          size="small"
          aria-label={`Retreat history for ${name}`}
          onClick={() => onHistory(r)}
          sx={{ ...retreatActionButtonSx, color: DASHBOARD_NAVY }}
        >
          <History size={17} />
        </IconButton>
      </Tooltip>
      {kind === "projected" && isSuperAdmin && (
        <>
          <RetreatEditIconButton
            title="View or edit projected retreat"
            ariaLabel={`View or edit projected retreat for ${name}`}
            to={`/retreats/${r.id}/project`}
          />
          <RetreatDeleteIconButton
            ariaLabel={`Delete retreat record for ${name}`}
            onClick={() => onDelete(r)}
          />
        </>
      )}
      {kind === "inSetup" && (
        <>
          <RetreatEditIconButton
            title="View or edit retreat setup"
            ariaLabel={`View or edit setup for ${name}`}
            to={`/retreat-setup/${r.id}`}
          />
          {isSuperAdmin && (r.readinessPercent || 0) >= 100 && (
            <Tooltip title="Approve & Publish" placement="bottom" arrow>
              <IconButton
                size="small"
                aria-label={`Approve and publish ${name}`}
                onClick={() => onApprove(r)}
                sx={{ ...retreatActionButtonSx, color: "#2e7d32" }}
              >
                <i
                  className="mdi mdi-check-circle text-lg font-size"
                  style={{ fontSize: 17 }}
                />
              </IconButton>
            </Tooltip>
          )}
          {isSuperAdmin && (
            <RetreatDeleteIconButton
              ariaLabel={`Delete retreat record for ${name}`}
              onClick={() => onDelete(r)}
            />
          )}
        </>
      )}
      {kind === "completed" && (
        <RetreatEditIconButton
          title="View or edit retreat setup"
          ariaLabel={`View or edit setup for ${name}`}
          to={`/retreat-setup/${r.id}`}
        />
      )}
      {kind === "deleted" && isSuperAdmin && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<RotateCcw size={14} />}
          onClick={() => onRestore(r)}
          sx={dashboardOutlinedButtonSx}
        >
          Restore
        </Button>
      )}
    </Stack>
  );
}
